import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { addKeyToLokalise, fetchNamespaceTranslations, isValidTranslationKey, REQUESTS_NAMESPACE, wasSentToLokalise } from '../lib/lokalise';

type Status = 'checking' | 'exists' | 'missing' | 'unknown' | 'sending' | 'sent';

export interface AddToLokaliseButtonProps {
  translationKey?: string | null;
  namespace?: string;
}

export const AddToLokaliseButton = ({ translationKey, namespace = REQUESTS_NAMESPACE }: AddToLokaliseButtonProps) => {
  const notify = useNotify();
  const key = translationKey ?? '';
  const [status, setStatus] = useState<Status>('checking');
  const [baseValue, setBaseValue] = useState<string>();

  useEffect(() => {
    if (!key || !isValidTranslationKey(key)) return undefined;
    if (wasSentToLokalise(namespace, key)) {
      setStatus('sent');
      return undefined;
    }
    let cancelled = false;
    setStatus('checking');
    fetchNamespaceTranslations(namespace)
      .then((translations) => {
        if (cancelled) return;
        setBaseValue(translations[key]);
        setStatus(key in translations ? 'exists' : 'missing');
      })
      .catch(() => {
        if (!cancelled) setStatus('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, [key, namespace]);

  if (!key) return null;

  if (!isValidTranslationKey(key)) {
    return (
      <Tooltip title="Translation keys can't have leading/trailing spaces or contain '|'">
        <Chip size="small" color="error" variant="outlined" label="Invalid key" />
      </Tooltip>
    );
  }

  if (status === 'checking') return <CircularProgress size={16} />;

  if (status === 'exists') {
    return (
      <Tooltip title={`${namespace} · en: ${baseValue ?? ''}`}>
        <Chip size="small" color="success" variant="outlined" label="In Lokalise" />
      </Tooltip>
    );
  }

  if (status === 'sent') {
    return (
      <Tooltip title={`Added to ${namespace}. Translate it in Lokalise and run Build ("Empty translations" → "Replace with base language") to make it live.`}>
        <Chip size="small" color="info" variant="outlined" label="Sent to Lokalise" />
      </Tooltip>
    );
  }

  const send = async () => {
    setStatus('sending');
    try {
      await addKeyToLokalise(namespace, key);
      setStatus('sent');
      notify(`"${key}" sent to Lokalise (${namespace})`, { type: 'success' });
    } catch (e) {
      setStatus('missing');
      notify(`Failed to send "${key}" to Lokalise: ${(e as Error).message}`, { type: 'error' });
    }
  };

  const tooltip = status === 'unknown' ? `Couldn't check whether the key already exists in ${namespace}` : `Key is missing from ${namespace}`;
  return (
    <Tooltip title={tooltip}>
      <span>
        <Button size="small" variant="outlined" onClick={send} disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Add to Lokalise'}
        </Button>
      </span>
    </Tooltip>
  );
};

export default AddToLokaliseButton;
